import { HttpApiService } from "@/shared/services/httpApi.service";

import { ACCESS_ENDPOINTS } from "../constants/accessCatalog.constants";
import type {
  AccessPaginated,
  AccessPolicy,
  CreatePolicyRequest,
  UpdatePolicyRequest,
} from "../types/access.types";

const POLICIES_PAGE_SIZE = 100;
/** Safety bound on pagination loops. */
const MAX_POLICY_PAGES = 50;

/**
 * `GET /internal/policies` — reusable allow/deny permission bundles (each with its `statements`). Consumed by
 * the role editor (to link policies) and, later, by the Policies section.
 */
export const policiesService = {
  list: async (skip = 0, limit = POLICIES_PAGE_SIZE): Promise<AccessPaginated<AccessPolicy>> => {
    const response = await HttpApiService.get<AccessPaginated<AccessPolicy>>(ACCESS_ENDPOINTS.POLICIES, {
      params: { skip, limit },
    });
    return response.data;
  },

  /** Fetch the whole catalog by paging through until exhausted (bounded). */
  listAll: async (): Promise<AccessPolicy[]> => {
    const all: AccessPolicy[] = [];
    let skip = 0;
    for (let page = 0; page < MAX_POLICY_PAGES; page++) {
      const result = await policiesService.list(skip, POLICIES_PAGE_SIZE);
      all.push(...result.data);
      if (!result.hasMore || result.data.length === 0) break;
      skip += POLICIES_PAGE_SIZE;
    }
    return all;
  },

  create: async (body: CreatePolicyRequest): Promise<AccessPolicy> => {
    const response = await HttpApiService.post<AccessPolicy, CreatePolicyRequest>(
      ACCESS_ENDPOINTS.POLICIES,
      body,
    );
    return response.data;
  },

  update: async (id: string, body: UpdatePolicyRequest): Promise<AccessPolicy> => {
    const response = await HttpApiService.patch<AccessPolicy, UpdatePolicyRequest>(
      ACCESS_ENDPOINTS.policyById(id),
      body,
    );
    return response.data;
  },

  /** `DELETE /internal/policies/:id` — backend returns 409 if linked to a role. */
  remove: async (id: string): Promise<void> => {
    await HttpApiService.delete(ACCESS_ENDPOINTS.policyById(id));
  },
};
